import { useState, useMemo } from "react";

interface UseClientPaginationProps<T> {
  items: T[];
  initialPageSize?: number;
}

interface UseClientPaginationReturn<T> {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  paginatedItems: T[];
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Client-side pagination hook
 * Provides pagination controls and paginated data slice
 * 
 * @example
 * const { paginatedItems, currentPage, totalPages, setCurrentPage } = 
 *   useClientPagination({ items: myData, initialPageSize: 50 });
 */
export function useClientPagination<T>({
  items,
  initialPageSize = 50,
}: UseClientPaginationProps<T>): UseClientPaginationReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const totalPages = Math.ceil(items.length / pageSize);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return items.slice(startIndex, endIndex);
  }, [items, currentPage, pageSize]);

  const handleSetPageSize = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page on page size change
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);

  return {
    currentPage,
    pageSize,
    totalPages,
    paginatedItems,
    setCurrentPage,
    setPageSize: handleSetPageSize,
    goToFirstPage,
    goToLastPage,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  };
}
